import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { authService, apiService } from '../lib/api'
import toast from 'react-hot-toast'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionValidated, setSessionValidated] = useState(false)

  const clearSession = useCallback(() => {
    setUser(null)
    setSessionValidated(true)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
  }, [])

  const handleUnauthorized = useCallback(() => {
    clearSession()
    toast.error('Your session has expired. Please log in again.')
  }, [clearSession])

  // Validate existing token with backend on app start
  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('access_token')

      if (!token) {
        setLoading(false)
        setSessionValidated(true)
        return
      }

      try {
        // Call /auth/me to verify the token is still valid
        const data = await authService.getMe()
        
        if (data.user) {
          setUser(data.user)
          localStorage.setItem('user_data', JSON.stringify(data.user))
        } else {
          clearSession()
        }
      } catch (error) {
        console.error('Session validation failed:', error.message)
        clearSession()
      } finally {
        setLoading(false)
        setSessionValidated(true)
      }
    }

    // Register the unauthorized handler for 401 responses from API
    apiService.setOnUnauthorized(handleUnauthorized)

    validateSession()
  }, [clearSession, handleUnauthorized])

  const signUp = async (email, password) => {
    try {
      setLoading(true)
      const data = await authService.register(email, password)
      
      if (data.user) {
        setUser(data.user)
        localStorage.setItem('user_data', JSON.stringify(data.user))
        toast.success('Account created successfully!')
      }

      return { data, error: null }
    } catch (error) {
      console.error('Error signing up:', error)
      toast.error(error.message || 'Failed to create account')
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const data = await authService.login(email, password)
      
      if (data.user) {
        setUser(data.user)
        localStorage.setItem('user_data', JSON.stringify(data.user))
        toast.success('Successfully signed in!')
      }

      return { data, error: null }
    } catch (error) {
      console.error('Error signing in:', error)
      toast.error(error.message || 'Failed to sign in')
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      await authService.logout()
      clearSession()
      toast.success('Successfully signed out!')
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error(error.message || 'Failed to sign out')
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      // For now, just show a message since we don't have password reset implemented in Flask
      toast.success('Password reset functionality coming soon!')
      return { error: null }
    } catch (error) {
      console.error('Error resetting password:', error)
      toast.error(error.message)
      return { error }
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      // For now, just show a message since we don't have password update implemented in Flask
      toast.success('Password update functionality coming soon!')
      return { error: null }
    } catch (error) {
      console.error('Error updating password:', error)
      toast.error(error.message)
      return { error }
    }
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
